/**
 * Kas op /financieel — twee losse rollen, bewust gescheiden.
 *
 * `KasBron`   : de kas als bron ín Cash Positie, in exact dezelfde opmaak als
 *               bankrekeningen en payment providers (regels + subtotaal). Geen
 *               eigen koptekst, geen groot getal, geen knoppen.
 * `KasBeheer` : de managementkant (tellen, stortingshistorie, signaal dat er
 *               lang niet gestort is) als eigen kaart onder Cash Positie.
 *
 * Waarom gesplitst: /kas is de werkvloer (medewerker tekent af wat de chauffeur
 * inlevert), /controle stemt de dag af, en /financieel is de positie. Deze
 * component hoort alleen bij die laatste laag. Rekenmodel:
 * `api/_lib/finance/kas-saldo.ts`.
 */
import { useEffect, useState } from 'react';
import { useKasSaldoStore, type KasStorting } from '../../stores/kas-saldo-store';

function formatCurrency(amount: number): string {
  return new Intl.NumberFormat('nl-NL', { style: 'currency', currency: 'EUR' }).format(amount);
}

function formatDateKort(dateStr: string): string {
  return new Date(dateStr + 'T12:00:00').toLocaleDateString('nl-NL', { day: 'numeric', month: 'short' });
}

function formatDate(dateStr: string): string {
  return new Date(dateStr + 'T12:00:00').toLocaleDateString('nl-NL', {
    day: 'numeric',
    month: 'long',
    year: 'numeric',
  });
}

function vandaag(): string {
  const d = new Date();
  return d.getFullYear() + '-' + String(d.getMonth() + 1).padStart(2, '0') + '-' + String(d.getDate()).padStart(2, '0');
}

/** Zelfde visuele taal als SourceIndicator bij de bankrekeningen. */
function KasIndicator() {
  const saldo = useKasSaldoStore((s) => s.saldo);
  if (!saldo) return null;

  if (!saldo.heeftAnker) {
    return <span className="text-xs text-amber-600">niet geteld</span>;
  }
  if (!saldo.controle.houdbaar) {
    return <span className="text-xs text-red-600">startsaldo klopt niet</span>;
  }
  const label = saldo.ankerSoort === 'aanname' ? 'aangenomen' : 'geteld';
  return (
    <span className="text-xs text-gray-400">
      {label} {formatDateKort(saldo.anker!.datum)}
    </span>
  );
}

/**
 * De kas als bron in Cash Positie. Telt alleen mee in het kaarttotaal als het
 * saldo bruikbaar is; is dat niet zo, dan zegt de subtotaalregel dat er ook bij
 * in plaats van een getal te tonen dat stilletjes buiten het totaal valt.
 */
export function KasBron() {
  const { saldo, isLoading, error, laadSaldo } = useKasSaldoStore();

  useEffect(() => {
    laadSaldo();
  }, [laadSaldo]);

  if (error) return <p className="text-sm text-red-600">{error}</p>;
  if (!saldo) return <p className="text-sm text-gray-400">{isLoading ? 'Laden…' : 'Geen kasdata'}</p>;

  const teltMee = saldo.heeftAnker && saldo.controle.houdbaar;

  return (
    <div className="space-y-2">
      <div className="flex items-center justify-between py-1.5">
        <div className="flex items-center gap-2">
          <span className="text-sm text-main">In de kluis</span>
          <KasIndicator />
        </div>
        <span className="text-sm font-medium tabular-nums">{formatCurrency(saldo.inKluis)}</span>
      </div>

      {saldo.nogNietAfgetekend > 0 ? (
        <div className="flex items-center justify-between py-1.5">
          <div className="flex items-center gap-2">
            <span className="text-sm text-main">Nog bij een chauffeur</span>
            <span className="text-xs text-gray-400">niet afgetekend</span>
          </div>
          <span className="text-sm font-medium tabular-nums">{formatCurrency(saldo.nogNietAfgetekend)}</span>
        </div>
      ) : null}

      <div className="flex items-center justify-between py-1.5 border-t border-gray-100">
        <div className="flex items-center gap-2">
          <span className="text-sm font-medium text-muted">Subtotaal kas</span>
          {teltMee ? null : <span className="text-xs text-amber-600">telt niet mee in het totaal</span>}
        </div>
        <span className={`text-sm font-semibold tabular-nums ${teltMee ? '' : 'text-gray-400'}`}>
          {formatCurrency(saldo.saldo)}
        </span>
      </div>
    </div>
  );
}

function TelForm({ verwacht, onSluit }: { verwacht: number | null; onSluit: () => void }) {
  const { legTellingVast, isSaving } = useKasSaldoStore();
  const [bedrag, setBedrag] = useState('');
  const [teldatum, setTeldatum] = useState(vandaag);
  const [opmerking, setOpmerking] = useState('');
  const [isAanname, setIsAanname] = useState(false);
  const [verschil, setVerschil] = useState<number | null>(null);

  const handleOpslaan = async () => {
    const n = parseFloat(bedrag.replace(',', '.'));
    if (!Number.isFinite(n) || n < 0) return;
    const uitkomst = await legTellingVast(teldatum, n, opmerking || undefined, isAanname ? 'aanname' : 'telling');
    if (uitkomst === 'fout') return;
    if (uitkomst === null) {
      onSluit();
      return;
    }
    setVerschil(uitkomst);
  };

  if (verschil !== null) {
    const klopt = Math.abs(verschil) < 0.01;
    return (
      <div className="p-4 bg-gray-50 border border-gray-200 rounded-lg space-y-3">
        <p className="text-sm text-main">
          {klopt ? (
            <>De telling klopt precies met wat het systeem verwachtte.</>
          ) : (
            <>
              Geteld is <strong>{formatCurrency(verschil)}</strong> {verschil > 0 ? 'méér' : 'minder'} dan verwacht.
              Het saldo loopt vanaf nu verder vanaf deze telling.
            </>
          )}
        </p>
        <button onClick={onSluit} className="px-3 py-1.5 text-sm bg-indigo-600 text-white rounded-lg hover:bg-indigo-700">
          Sluiten
        </button>
      </div>
    );
  }

  return (
    <div className="p-4 bg-gray-50 border border-gray-200 rounded-lg space-y-3">
      <p className="text-sm text-muted">
        Tel het contante geld in de kluis en vul het totaal in. Hoort er geld bij dat pas na de
        teldatum binnenkwam, kies dan de datum waarop de telling geldt.
        {verwacht !== null ? <> Verwacht nu: <strong>{formatCurrency(verwacht)}</strong>.</> : null}
      </p>
      <div className="flex flex-wrap items-center gap-2">
        <input
          type="date"
          value={teldatum}
          max={vandaag()}
          onChange={(e) => setTeldatum(e.target.value)}
          className="text-sm border border-gray-300 rounded-lg px-2 py-1.5"
        />
        <input
          type="text"
          inputMode="decimal"
          placeholder="Geteld bedrag"
          value={bedrag}
          onChange={(e) => setBedrag(e.target.value)}
          className="w-40 text-sm border border-gray-300 rounded-lg px-2 py-1.5 focus:outline-none focus:ring-1 focus:ring-indigo-500"
          autoFocus
        />
        <input
          type="text"
          placeholder="Opmerking (optioneel)"
          value={opmerking}
          onChange={(e) => setOpmerking(e.target.value)}
          className="flex-1 min-w-[160px] text-sm border border-gray-300 rounded-lg px-2 py-1.5 focus:outline-none focus:ring-1 focus:ring-indigo-500"
        />
        <button
          onClick={handleOpslaan}
          disabled={isSaving || !bedrag}
          className="px-3 py-1.5 text-sm bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 disabled:opacity-50"
        >
          {isSaving ? 'Opslaan…' : 'Vastleggen'}
        </button>
        <button onClick={onSluit} className="px-3 py-1.5 text-sm text-gray-500 hover:text-gray-700">
          Annuleer
        </button>
      </div>
      <label className="flex items-center gap-2 text-xs text-muted">
        <input type="checkbox" checked={isAanname} onChange={(e) => setIsAanname(e.target.checked)} />
        Dit is een aanname, niet fysiek geteld (het saldo blijft dan een schatting)
      </label>
    </div>
  );
}

function StortingenDialog({
  stortingen,
  isOpen,
  onSluit,
}: {
  stortingen: KasStorting[];
  isOpen: boolean;
  onSluit: () => void;
}) {
  if (!isOpen) return null;

  return (
    <div
      className="fixed inset-0 z-[70] flex items-center justify-center bg-black/40"
      onClick={onSluit}
    >
      <div
        className="bg-white rounded-lg shadow-xl max-w-md w-full mx-4 max-h-[80vh] overflow-hidden flex flex-col"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="px-6 py-4 border-b border-gray-200">
          <h2 className="text-base font-semibold text-main">Stortingen</h2>
          <p className="text-xs text-muted mt-1">
            {stortingen.length} storting{stortingen.length !== 1 ? 'en' : ''} in de historie
          </p>
        </div>

        <div className="flex-1 overflow-y-auto px-6 py-3 space-y-1">
          {stortingen.map((s) => (
            <div key={s.id} className="flex items-center justify-between text-sm py-2 border-b border-gray-50 last:border-0">
              <div className="flex items-center gap-2 min-w-0">
                <span className="text-main">{formatDate(s.stortingDatum)}</span>
                <span className="text-xs text-gray-400 shrink-0">{s.bron === 'bank' ? 'uit bank' : 'handmatig'}</span>
              </div>
              <span className="tabular-nums text-main shrink-0 ml-3">{formatCurrency(s.bedrag)}</span>
            </div>
          ))}
        </div>

        <div className="px-6 py-3 border-t border-gray-200 flex justify-end">
          <button
            type="button"
            onClick={onSluit}
            className="px-4 py-1.5 text-sm text-gray-600 hover:text-gray-900"
          >
            Sluiten
          </button>
        </div>
      </div>
    </div>
  );
}

function StortForm({ onSluit }: { onSluit: () => void }) {
  const { legStortingVast, isSaving } = useKasSaldoStore();
  const [bedrag, setBedrag] = useState('');
  const [datum, setDatum] = useState(vandaag);

  const handleOpslaan = async () => {
    const n = parseFloat(bedrag.replace(',', '.'));
    if (!Number.isFinite(n) || n <= 0) return;
    if (await legStortingVast(datum, n)) onSluit();
  };

  return (
    <div className="p-4 bg-gray-50 border border-gray-200 rounded-lg space-y-3">
      <p className="text-sm text-muted">
        Stortingen bij de Geldmaat pikt de bankkoppeling zelf op. Voeg er hier alleen één toe als
        die er na een paar dagen nog niet tussen staat.
      </p>
      <div className="flex flex-wrap items-center gap-2">
        <input
          type="date"
          value={datum}
          max={vandaag()}
          onChange={(e) => setDatum(e.target.value)}
          className="text-sm border border-gray-300 rounded-lg px-2 py-1.5"
        />
        <input
          type="text"
          inputMode="decimal"
          placeholder="Gestort bedrag"
          value={bedrag}
          onChange={(e) => setBedrag(e.target.value)}
          className="w-40 text-sm border border-gray-300 rounded-lg px-2 py-1.5 focus:outline-none focus:ring-1 focus:ring-indigo-500"
          autoFocus
        />
        <button
          onClick={handleOpslaan}
          disabled={isSaving || !bedrag}
          className="px-3 py-1.5 text-sm bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 disabled:opacity-50"
        >
          {isSaving ? 'Opslaan…' : 'Toevoegen'}
        </button>
        <button onClick={onSluit} className="px-3 py-1.5 text-sm text-gray-500 hover:text-gray-700">
          Annuleer
        </button>
      </div>
    </div>
  );
}

/** Managementkaart: tellen, stortingshistorie, en het signaal dat er lang niet gestort is. */
export function KasBeheer() {
  const { saldo, error, laadSaldo } = useKasSaldoStore();
  const [formulier, setFormulier] = useState<'geen' | 'tellen' | 'storten'>('geen');
  const [stortingenOpen, setStortingenOpen] = useState(false);

  useEffect(() => {
    laadSaldo();
  }, [laadSaldo]);

  if (error || !saldo) return null;

  const stortingAchterstallig = (saldo.dagenSindsStorting ?? 0) > 60;

  return (
    <div className="bg-white rounded-card shadow-card p-6">
      <div className="flex items-center justify-between mb-4">
        <h2 className="text-lg font-semibold text-main">Kas</h2>
        <div className="flex items-center gap-2">
          <button
            onClick={() => setFormulier(formulier === 'tellen' ? 'geen' : 'tellen')}
            className="px-3 py-1.5 text-sm text-indigo-600 border border-indigo-200 rounded-lg hover:bg-indigo-50"
          >
            Kas tellen
          </button>
          <button
            onClick={() => setFormulier(formulier === 'storten' ? 'geen' : 'storten')}
            className="px-3 py-1.5 text-sm text-gray-600 border border-gray-200 rounded-lg hover:bg-gray-50"
          >
            Storting toevoegen
          </button>
        </div>
      </div>

      <div className="space-y-3">
        {!saldo.heeftAnker ? (
          <div className="px-3 py-2.5 bg-amber-50 border border-amber-200 rounded-lg">
            <p className="text-xs text-amber-800">
              De kas is nog nooit geteld, dus dit is een mutatie en geen saldo. De contante
              ontvangsten lopen terug tot 2023 en de stortingshistorie maar tot januari 2026, dus
              het startsaldo is niet af te leiden. Tel de kas één keer; daarna klopt het getal.
            </p>
          </div>
        ) : null}

        {saldo.heeftAnker && !saldo.controle.houdbaar ? (
          <div className="px-3 py-2.5 bg-red-50 border border-red-200 rounded-lg">
            <p className="text-xs text-red-800">
              <strong>Dit startsaldo kan niet kloppen.</strong> Op{' '}
              {saldo.controle.diepsteDatum ? formatDate(saldo.controle.diepsteDatum) : 'enig moment'} komt de
              kas uit op {formatCurrency(saldo.controle.diepsteSaldo)}, en een kas kan niet negatief staan.
              Het startsaldo is dus minstens {formatCurrency(saldo.controle.tekort)} te laag: het laagste
              bedrag dat wél kan is {formatCurrency(saldo.controle.minimaalAnker)}. Andere verklaring: er
              verlaat contant geld de kas buiten de stortingen om.
            </p>
          </div>
        ) : null}

        {formulier === 'tellen' ? (
          <TelForm verwacht={saldo.heeftAnker ? saldo.saldo : null} onSluit={() => setFormulier('geen')} />
        ) : null}
        {formulier === 'storten' ? <StortForm onSluit={() => setFormulier('geen')} /> : null}

        {saldo.laatsteStorting ? (
          <div
            className={`px-3 py-2.5 rounded-lg border ${
              stortingAchterstallig ? 'bg-amber-50 border-amber-200' : 'bg-gray-50 border-gray-200'
            }`}
          >
            <p className={`text-xs ${stortingAchterstallig ? 'text-amber-800' : 'text-muted'}`}>
              Laatst gestort op {formatDate(saldo.laatsteStorting.datum)} (
              {formatCurrency(saldo.laatsteStorting.bedrag)}), {saldo.dagenSindsStorting} dagen geleden.
              Sindsdien is er <strong>{formatCurrency(saldo.contantSindsStorting)}</strong> aan contant geld
              binnengekomen.
            </p>
            {saldo.stortingen.length > 1 ? (
              <button
                type="button"
                onClick={() => setStortingenOpen(true)}
                className="mt-2 text-xs text-indigo-600 hover:text-indigo-700 hover:underline"
              >
                Bekijk stortingshistorie ({saldo.stortingen.length})
              </button>
            ) : null}
          </div>
        ) : saldo.stortingen.length > 0 ? (
          <button
            type="button"
            onClick={() => setStortingenOpen(true)}
            className="text-xs text-indigo-600 hover:text-indigo-700 hover:underline"
          >
            Bekijk stortingshistorie ({saldo.stortingen.length})
          </button>
        ) : null}
      </div>

      <StortingenDialog
        stortingen={saldo.stortingen}
        isOpen={stortingenOpen}
        onSluit={() => setStortingenOpen(false)}
      />
    </div>
  );
}
